/** Мелкие формататоры экрана. Вынесены, чтобы их можно было проверить. */

import { ApiError } from "./api";
import type { ProductSalon } from "./providers";

/**
 * Русское склонение существительного по числу: 1 / 2-4 / 5-20,0,5-9 —
 * с отдельной веткой на 11-14, которые ведут себя не как 1-4, хотя
 * оканчиваются на те же цифры (11 короб ов, не 11 короб).
 */
function pluralize(n: number, one: string, few: string, many: string): string {
  const last = n % 10;
  const two = n % 100;
  if (two >= 11 && two <= 14) return many;
  if (last === 1) return one;
  if (last >= 2 && last <= 4) return few;
  return many;
}

export function boxWord(n: number): string {
  return pluralize(n, "короб", "короба", "коробов");
}

export function productWord(n: number): string {
  return pluralize(n, "товар", "товара", "товаров");
}

export function pickWord(n: number): string {
  return pluralize(n, "отбор", "отбора", "отборов");
}

export function sizeWord(n: number): string {
  return pluralize(n, "размер", "размера", "размеров");
}

export function barcodeWord(n: number): string {
  return pluralize(n, "баркод", "баркода", "баркодов");
}

export function salonWord(n: number): string {
  return pluralize(n, "салон", "салона", "салонов");
}

/**
 * ПЕРЕМЕЩЕНИЕ — одна строка `sMove`: вещь уехала из места в место.
 *
 * Слово именно это, а не «переезд» (замечание владельца 09.09.26). В CRM
 * операция зовётся перемещением, дверь у нас `POST /api/move` «перемещение»,
 * раннер тоже; «переезд» было нашим домашним словом на одних только экранах, и
 * склад на разборе читал два имени одной вещи.
 */
export function moveWord(n: number): string {
  return pluralize(n, "перемещение", "перемещения", "перемещений");
}

export function placeWord(n: number): string {
  return pluralize(n, "место", "места", "мест");
}

export function findingWord(n: number): string {
  return pluralize(n, "находка", "находки", "находок");
}

/** ЗАДАЧА — одна единица сборки FBS: снять с полки и упаковать одну вещь. */
export function taskWord(n: number): string {
  return pluralize(n, "задача", "задачи", "задач");
}

/**
 * ПОЗИЦИЯ — один баркод в коробе, а не одна вещь.
 *
 * Слово нужно отдельное, потому что в разделе «Короба» рядом стоят два числа
 * разной природы: «29 позиций» и «29 штук» совпали случайно (в коробах лежит
 * в основном по штуке на баркод), а означают разное — сколько РАЗНЫХ товаров
 * и сколько ВЕЩЕЙ. Назови оба «товарами» — и колонки стали бы неразличимы.
 */
export function positionWord(n: number): string {
  return pluralize(n, "позиция", "позиции", "позиций");
}

/**
 * ПОСТАВКА — одна отгрузка маркетплейсу: акт перевозки Ozon или поставка ВБ.
 *
 * Слово общее на обе платформы намеренно: документы у них разные, но вопрос
 * раздела «FBS Короба» один — сколько разных отгрузок стоит за коробами. 87
 * коробов в 19 поставках и те же 87 в одной — совершенно разная работа.
 */
export function supplyWord(n: number): string {
  return pluralize(n, "поставка", "поставки", "поставок");
}

/** ВЕЩЬ — одна серия склада. «Штук» не склоняется, а «вещей» приходится. */
export function thingWord(n: number): string {
  return pluralize(n, "вещь", "вещи", "вещей");
}

/**
 * СПИСАНИЕ — одна строка `sSerial_spisanie`: одну вещь сняли с учёта.
 *
 * Считаются им ОБЕ стороны операции — и списание, и возврат вещи в оборот
 * (`available = 1`): в CRM это строки одной таблицы, и раздел показывает их
 * одним списком. Слово поэтому про саму запись, а не про её направление.
 */
export function spisanieWord(n: number): string {
  return pluralize(n, "списание", "списания", "списаний");
}

/**
 * ПРИЧИНА — категория списания (`sSerial_spisanie_category`).
 *
 * Слово на экране именно это, а не «категория»: у места спрашивают, ПОЧЕМУ
 * отсюда пропадают вещи, и «три причины» отвечает на вопрос, а «три категории»
 * рассказывает про справочник CRM.
 */
export function reasonWord(n: number): string {
  return pluralize(n, "причина", "причины", "причин");
}

/**
 * Категория для СТРОКИ списка — последнее звено пути.
 *
 * В CRM категория хранится путём целиком («Аксессуары / Украшения на голову /
 * Повязки на голову»), и в строке товара он занял бы больше места, чем само
 * имя товара. В фильтре путь остаётся полным — там по нему выбирают, и две
 * категории различаются как раз серединой; здесь же он приезжает подсказкой
 * (`title`), поэтому ничего не теряется.
 */
export function categoryShort(name: string): string {
  const parts = name.split("/");
  return (parts[parts.length - 1] ?? "").trim() || name;
}

/**
 * Местная дата в YYYY-MM-DD. Именно местная: `toISOString()` переводит в
 * UTC и для ночи по Москве отдаёт вчерашний день — отчёт молча показал бы
 * не тот период, который выбрали.
 */
export function isoDate(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

/**
 * Окно из пресета: `days` календарных дней, ВКЛЮЧАЯ сегодня. Отсюда −1:
 * «7 дней» — это сегодня и шесть предыдущих. Без него окно вышло бы
 * восьмидневным, а среднее в день — заниженным на восьмую часть.
 */
export function windowFrom(
  days: number,
  today: Date = new Date(),
): { from: string; to: string } {
  const from = new Date(today);
  from.setDate(from.getDate() - (days - 1));
  return { from: isoDate(from), to: isoDate(today) };
}

/**
 * Момент из CRM человеку: `2026-09-08 03:43:22` → `08.09.2026 03:43`.
 *
 * Разбор СТРОКОЙ, а не через `new Date(...)`, и это не мелочь. CRM отдаёт
 * время уже местным, без зоны; `Date` прочитал бы такую строку по правилам
 * браузера и на телефоне с чужой зоной сдвинул бы час переезда — то есть
 * соврал бы ровно там, где карточку серии и открывают: «когда это уехало».
 *
 * Секунды убраны: на складе важны минуты. Непонятную строку возвращаем как
 * есть — лучше показать сырое значение, чем прочерк вместо факта.
 */
export function stamp(at: string | null | undefined): string {
  const text = (at ?? "").trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(text);
  if (!m) return text;
  return `${m[3]}.${m[2]}.${m[1]} ${m[4]}:${m[5]}`;
}

/**
 * СУТКИ из CRM человеку: `2026-09-05` → `05.09.2026`.
 *
 * Отдельно от `stamp`, а не «частный случай» его: у даты поставки нет часа
 * вовсе (`date` в CRM), и подставить к ней полночь значило бы соврать, что
 * поставку отгрузили в 00:00. По той же причине сутки не переводятся в
 * московское время (`app/core/tz.py`): часового сдвига у них нет, и «перевод»
 * сделал бы их предыдущим днём.
 */
export function day(at: string | null | undefined): string {
  const text = (at ?? "").trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
  if (!m) return text;
  return `${m[3]}.${m[2]}.${m[1]}`;
}

/**
 * Отборов в день. Один знак после запятой: второй на складе никому не
 * нужен, а целое число слепило бы 1.4 и 0.6 в одну «1».
 *
 * Отдельная ветка на почти-ноль: округление до 0.0 читается как «не
 * заказывают вовсе», и товар, за которым ходят раз в двадцать дней, стал
 * бы неотличим от мёртвого остатка.
 */
export function perDay(n: number): string {
  if (n === 0) return "0";
  if (n < 0.1) return "<0.1";
  return n.toFixed(1);
}

/**
 * Номера резервных коробов: сколько показать чипами и сколько осталось.
 *
 * Три — не произвол: замер 25.08.26 по 61 строке отчёта дал медиану 0 и
 * максимум 12, строк с резервом больше трёх — 7. То есть три номера
 * закрывают 54 строки из 61 без сворачивания вовсе.
 *
 * Остаток отдаётся числом, а не отрезается: скрытые значения должны быть
 * доступны, а не потеряны (правило Chip Collection Reflow).
 */
export function reserveChips(
  nums: number[],
  max = 3,
): { shown: number[]; rest: number } {
  return { shown: nums.slice(0, max), rest: Math.max(0, nums.length - max) };
}

/**
 * Число КОРОБОВ, которые стоит переносить — уникальные box_barcode.
 * Строк в отчёте больше или равно числу коробов: строка — это карточка
 * товара внутри короба, в одном коробе может лежать несколько товаров.
 * Путать rows.length с числом коробов — обещать сотруднику перенести
 * больше коробов, чем реально нужно.
 */
export function uniqueBoxCount(rows: { box_barcode: string }[]): number {
  return new Set(rows.map((r) => r.box_barcode)).size;
}

/**
 * Отбор строк по номеру модели и номеру карточки.
 *
 * Совпадение по ПОДСТРОКЕ, а не по равенству: сотрудник набирает первые
 * цифры номера, который видит на ярлыке, и ждёт, что список сузится уже
 * на них. Требование ввести номер целиком превратило бы фильтр в поиск,
 * которым пользуются только зная ответ заранее.
 *
 * Пустое поле не фильтрует. Оба поля заданы — условия складываются (И), а
 * не выбираются: два разных идентификатора одной строки, и «модель 14625
 * ИЛИ карточка 7» не отвечает ни на один вопрос склада.
 *
 * Фильтр — это ЛИНЗА поверх готового отчёта, а не пересчёт: ранг,
 * покрытие и сводка наверху остаются от полного отчёта. Иначе, спрятав
 * половину строк, сотрудник увидел бы, что оставшиеся «закрывают» больше
 * отборов, чем на самом деле.
 */
export function filterRows<T extends { model_id: number; card_id: number }>(
  rows: T[],
  model: string,
  card: string,
): T[] {
  const m = model.trim();
  const c = card.trim();
  if (!m && !c) return rows;
  return rows.filter(
    (r) =>
      (!m || String(r.model_id).includes(m)) &&
      (!c || String(r.card_id).includes(c)),
  );
}

/** Разбор места по размерам: столько-то штук такой-то карточки. */
type CardUnits = { card_id: number; units: number };

/**
 * Штук ВЫБРАННОГО размера в этом разборе. `null` — размер не выбран, и
 * тогда ноль: «сколько его тут» — вопрос, который без выбора не задан.
 */
export function cardUnits(
  by_card: CardUnits[],
  cardId: number | null,
): number {
  if (cardId === null) return 0;
  return by_card.find((item) => item.card_id === cardId)?.units ?? 0;
}

/** Штук выбранного размера в салоне — сумма по всем его группам мест. */
export function salonCardUnits(
  salon: ProductSalon,
  cardId: number | null,
): number {
  return salon.groups.reduce(
    (sum, group) => sum + cardUnits(group.by_card, cardId),
    0,
  );
}

/**
 * Короба по убыванию штук ВЫБРАННОГО размера.
 *
 * Сортировка, а не отбор, и это разные обещания. Отбор скрыл бы короба без
 * этого размера — вместе с ними «21 из 23» потеряло бы знаменатель, а короб,
 * куда размер докладывают, исчез бы с экрана целиком. Сортировка ставит
 * нужное наверх и оставляет остальное там, где оно есть.
 *
 * ЛИНЗА поверх готового разбора, а не второй запрос: пересортировывается то,
 * что уже приехало, поэтому числа не могут разойтись с полным видом.
 *
 * Порядок внутри равных сохраняется — `Array.prototype.sort` стабильна с
 * ES2019. Это несущее свойство, а не удача: короба приезжают уже
 * упорядоченными по убыванию своих штук, и короба без выбранного размера
 * обязаны остаться в этом порядке, а не перемешаться.
 */
export function sortBoxesByCard<T extends { by_card: CardUnits[] }>(
  boxes: T[],
  cardId: number | null,
): T[] {
  if (cardId === null) return boxes;
  return [...boxes].sort(
    (a, b) => cardUnits(b.by_card, cardId) - cardUnits(a.by_card, cardId),
  );
}

/**
 * Салоны по убыванию штук выбранного размера — и группы мест внутри каждого
 * тоже.
 *
 * Группы сортируются вместе с салонами намеренно: салон отвечает на вопрос
 * «в какой город ехать», группа — «куда там смотреть», и ответ на второй
 * вопрос обязан быть отсортирован тем же размером, что и первый.
 */
export function sortSalonsByCard(
  salons: ProductSalon[],
  cardId: number | null,
): ProductSalon[] {
  if (cardId === null) return salons;
  return salons
    .map((salon) => ({
      ...salon,
      groups: [...salon.groups].sort(
        (a, b) => cardUnits(b.by_card, cardId) - cardUnits(a.by_card, cardId),
      ),
    }))
    .sort((a, b) => salonCardUnits(b, cardId) - salonCardUnits(a, cardId));
}

/**
 * Разные причины отказа значат разное для сотрудника: истёкшая сессия —
 * войти заново, недоступный сервис — подождать. Общее «ошибка» прячет это
 * различие и заставляет гадать.
 */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 401) return "Сессия истекла — войдите заново";
    if (e.status === 429) return "Слишком много запросов, подождите минуту";
    if (e.status >= 500) return "Сервис недоступен, попробуйте позже";
    return `Не удалось получить отчёт (код ${e.status})`;
  }
  return "Сервис недоступен, попробуйте позже";
}

/**
 * Решает, заменять ли содержимое экрана ошибкой или показывать её рядом с
 * уже загруженными данными. Если данные уже есть, разовый сбой пересчёта
 * не должен стирать список, который человек читает прямо сейчас — стелется
 * только полноэкранная ошибка, когда показывать вообще нечего.
 */
export function errorDisplay(hasReport: boolean): "replace" | "inline" {
  return hasReport ? "inline" : "replace";
}
